/**
 * MainTrendTile — Plan 05-12 Task 1.
 *
 * Single big-number + optional sparkline + optional % chip + drilldown link.
 * Clones CroTrendTile structure with these extensions:
 *   - Reads MainMetricResponse (from /api/main/scorecard) not CroTrendResponse
 *   - Optional snap snapshots for sparkline (MAIN-13..18/21)
 *   - placeholder_text branch delegates to MainPlaceholderTile
 *
 * UX-07 4-state machine: loading / cache-warming / sf-unreachable / success
 * (mirrors CroTile + CroTrendTile verbatim).
 *
 * Per UI-SPEC §"MainTrendTile" anatomy:
 *   KPI label + KPI value (text-2xl bold) + sparkline (h-32) + % chip + drilldown link
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import { buildEchartsOption, type SparklineAxisMode } from '../../lib/echarts-builder.js';
import { formatPercentToTarget, targetChipVariant } from '../../lib/fiscal.js';
import { formatCountInt, formatCurrency } from '../../lib/format.js';
import { SKO_BASELINE } from '../../lib/main-tile-mapping.js';
import { computeSnapDelta } from '../../lib/snap-delta.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';
import { Tooltip } from '../ui/Tooltip.js';


import type { MainMetricResponse, MainSnapResponse } from '@saledash/shared';

interface MainTrendTileProps {
  metricId: string;
  title: string;
  /** Optional plain-English formula for the tooltip */
  tooltipFormula?: string | undefined;
  data: MainMetricResponse | undefined;
  /** Snapshot series for sparkline (from /api/main/metrics/snap/:metricId) */
  snapshots: MainSnapResponse['snapshots'];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

const chipBgClass: Record<'good' | 'warn' | 'bad', string> = {
  good: 'bg-good-bg text-good-text',
  warn: 'bg-warn-bg text-warn-text',
  bad: 'bg-bad-bg text-bad-text',
};

/**
 * Per-metric sparkline X-axis tick mode.
 *
 * quick-260625-nem Task 3 (user-confirmed): MAIN-13..18 are a single daily
 * point-in-time value, NOT a per-month/per-quarter breakdown. So the X-axis is
 * the SNAPSHOT DATE each measurement was taken ('M/D'), with a "pipeline over
 * time" axis title — reverting the hz9 Task 6 month/quarter ticks.
 */
const SPARKLINE_AXIS_MODE: Record<string, SparklineAxisMode> = {
  'MAIN-13': 'date',
  'MAIN-14': 'date',
  'MAIN-15': 'date',
  'MAIN-16': 'date',
  'MAIN-17': 'date',
  'MAIN-18': 'date',
  // MAIN-21 Stalled Deals: point-in-time snapshot trend — X-axis = snapshot date
  // ('M/D') (quick-260630-f7f Task 7 item 14) so the dated trend reads clearly.
  'MAIN-21': 'date',
  // (MAIN-08/10/47 — the lead-response band — removed 2026-09-21, quick-260921-m88.)
};

/** X-axis title for the snapshot-date trend tiles (quick-260625-nem Task 3/5/6). */
const SPARKLINE_AXIS_NAME: Record<string, string> = {
  'MAIN-13': 'pipeline over time',
  'MAIN-14': 'pipeline over time',
  'MAIN-15': 'pipeline over time',
  'MAIN-16': 'pipeline over time',
  'MAIN-17': 'pipeline over time',
  'MAIN-18': 'pipeline over time',
  // MAIN-21 Stalled Deals — point-in-time count over snapshot dates (item 14).
  'MAIN-21': 'snapshot date',
};

/**
 * Trend tiles that render a visible, labeled Y-axis (quick-260625-nem Task 6).
 * quick-260630-f7f Task 3 adds the lead-funnel %-tiles (item 7) and the Open
 * Mature Pipeline $ tiles (item 20) so their sparklines get a legible,
 * data-scaled Y-axis instead of a flat hidden one.
 */
const SPARKLINE_Y_AXIS_NAME: Record<string, string> = {
  // Item 7 — lead-funnel conversion tiles (data-band autoscale).
  'MAIN-22': 'lead → meeting %',
  'MAIN-23': 'lead → opp %',
  // Item 20 — Open Mature Pipeline (CQ/NQ/FY) currency tiles.
  'MAIN-14': 'mature $ (CQ)',
  'MAIN-16': 'mature $ (NQ)',
  'MAIN-18': 'mature $ (FY)',
  // Item 14 — Stalled Deals: a labeled count Y-axis on the trend chart.
  'MAIN-21': 'stalled deals',
};

/**
 * quick-260630-f7f Task 3 item 20: tiles whose visible Y-axis ticks render as
 * compact currency ($Nk / $N.NM) — the Open Mature Pipeline $ tiles.
 */
const SPARKLINE_Y_AXIS_CURRENCY = new Set<string>(['MAIN-14', 'MAIN-16', 'MAIN-18']);

/**
 * quick-260630-f7f Task 3 item 9: tiles whose Y-axis is pinned to the full
 * 0–100% scale (stored 0–1 ratio) so a drop like 80% → 50% is legible against
 * the whole band rather than auto-zoomed flat. Currently EMPTY — its only member
 * (MAIN-46) left the board 2026-09-21 (quick-260921-m88). Mechanism kept.
 */
const SPARKLINE_Y_AXIS_PERCENT_0_100 = new Set<string>([]);

/**
 * Metrics whose sparkline annotates n (sample size) per point via tooltip
 * (quick-260625-nem Task 5) — valueSuffix shows the unit. Currently EMPTY — the
 * weekly-cohort tiles that used it (MAIN-10/47) left the board 2026-09-21
 * (quick-260921-m88). Mechanism kept.
 */
const SPARKLINE_COUNT_ANNOTATED: Record<string, string> = {};

/**
 * Metrics where LOWER is better (quick-260625-hz9 Task 3a). For these, a value
 * ABOVE the target is BAD (red, downward delta), not good. Currently EMPTY — the
 * response-time tiles (MAIN-10/46/47) left the board 2026-09-21
 * (quick-260921-m88). Mechanism kept for the next lower-is-better tile.
 */
const LOWER_IS_BETTER = new Set<string>([]);

/**
 * Per-metric on-tile clarifier note (quick-260630-f7f). Rendered in the context
 * line so the tile self-explains. Used for:
 *   - MAIN-01 (item 2): the weekly meetings goal math "5/AE × 6 AEs = 30/wk".
 *   - MAIN-21 (item 14): the point-in-time caveat.
 */
const TILE_CONTEXT_NOTE: Record<string, string> = {
  'MAIN-01': '5 meetings/AE × 6 AEs = 30/wk',
  // item 14: stalled count is a point-in-time snapshot (fluctuates day to day),
  // not a strict week-over-week measure.
  'MAIN-21': 'point-in-time count; not strictly WoW',
};

/**
 * 2026-07-22 (John): "Coverage-style" pipeline tiles — Open/Mature pipeline for
 * CQ/NQ/FY (MAIN-13/14/15/16/18). These are restyled to match the CRO dashboard's
 * Pipeline-Coverage tiles (CRO-02/03/04): a clean big-number + WoW pill + (when a
 * target exists) a horizontal progress-to-target bar. The daily sparkline and the
 * "trend building" note are SUPPRESSED for these tiles — execs found the sparse,
 * still-accruing sparkline noisy vs the crisp CRO look. The WoW pill still rides
 * the snapshot series (computeSnapDelta), and the % chip / gap / progress bar all
 * render exactly as before when a target is present. MAIN-17 (Open Pipeline FY) is
 * NOT here — it renders as the dedicated pacing hero tile.
 */
const COVERAGE_STYLE_IDS = new Set<string>([
  'MAIN-13', 'MAIN-14', 'MAIN-15', 'MAIN-16', 'MAIN-18',
]);

/**
 * Minimum distinct daily snapshot points before the snapshot-date trend renders
 * as a chart (quick-260625-son Task 2). ~Two weeks of daily snapshots. Below
 * this, the sparkline is too sparse to read, so we show the big current value
 * plus a muted "trend building" note instead.
 */
const MIN_TREND_POINTS = 14;

/**
 * Tiles subject to the "trend building" gate (quick-260625-son Task 2, option a;
 * extended quick-260626-j5u Task 4). Until enough distinct snapshot points exist
 * we suppress the near-empty sparkline and show a muted "trend building" note.
 *
 * Per-metric minimum distinct points before the chart renders:
 *   - MAIN-13..18 (daily pipeline $ snaps): 14 (MIN_TREND_POINTS, ~2 weeks).
 * (The MAIN-08 daily gate and the MAIN-10/47 low weekly-cohort gate of 3 went
 * with the lead-response band, 2026-09-21, quick-260921-m88.)
 * A metric absent from this map is never gated.
 */
const TREND_BUILDING_MIN: Record<string, number> = {
  'MAIN-13': MIN_TREND_POINTS,
  'MAIN-14': MIN_TREND_POINTS,
  'MAIN-15': MIN_TREND_POINTS,
  'MAIN-16': MIN_TREND_POINTS,
  'MAIN-17': MIN_TREND_POINTS,
  'MAIN-18': MIN_TREND_POINTS,
};

/**
 * Format the KPI value based on unit.
 */
function formatMainValue(value: number, unit: MainMetricResponse['unit']): string {
  switch (unit) {
    case 'USD':
      return formatCurrency(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'days':
    case 'hours':
      return `${value.toFixed(1)} ${unit}`;
    case 'ratio':
      return value.toFixed(2);
    default:
      // 'count' / volume metrics — thousands-separated INTEGER, no decimals
      // (quick-260626-j5u Task 1). Was formatCount(value, 1) → "2740.0"/"39.0".
      return formatCountInt(value);
  }
}

export function MainTrendTile({
  metricId,
  title,
  tooltipFormula,
  data,
  snapshots,
  isLoading,
  isError,
  error,
  refetch,
}: MainTrendTileProps): React.JSX.Element {
  const tooltipContent = tooltipFormula ? (
    <>
      <p className="mb-1 font-semibold text-ink-900">{title}</p>
      <p>{tooltipFormula}</p>
    </>
  ) : (
    <p className="font-semibold text-ink-900">{title}</p>
  );

  // UX-07: cache-warming amber banner
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <CardContent>
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes. The sync worker will populate this tile on the next poll.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // UX-07: SF-unreachable
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
          {tooltipFormula && (
            <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
          )}
        </CardHeader>
        <CardContent>
          <SfUnreachableBanner onRetry={refetch} />
        </CardContent>
      </Card>
    );
  }

  // UX-07: loading → 3 skeletons
  if (isLoading || !data) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-8 w-24" data-testid="skeleton-value" />
            <Skeleton className="h-3 w-32" data-testid="skeleton-label" />
            <Skeleton className="h-14 w-full" data-testid="skeleton-chart" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Success — placeholder branch (data-driven): delegate to MainPlaceholderTile
  if (data.placeholder_text) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        <CardHeader>
          <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p
            className="text-sm text-ink-700"
            data-testid="main-placeholder-text"
          >
            {data.placeholder_text}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Success path
  const value = data.value ?? 0;
  const formattedValue = formatMainValue(value, data.unit);

  // % chip calculation
  const percentToTarget =
    data.target_value != null && data.target_value !== 0
      ? value / data.target_value
      : null;
  const hasTarget = data.target_value != null && percentToTarget != null;

  // quick-260625-hz9 Task 3a: for lower-is-better metrics (LOWER_IS_BETTER), being
  // ABOVE target is BAD. We flip the chip color ratio (target/value) so the
  // good/warn/bad bands invert, and flip the arrow + over/under-target coloring.
  const lowerIsBetter = LOWER_IS_BETTER.has(metricId);
  const chipRatio =
    percentToTarget !== null && lowerIsBetter && percentToTarget !== 0
      ? 1 / percentToTarget
      : percentToTarget;
  // "On track" = at/under target for lower-is-better, at/over target otherwise.
  const onTrack =
    percentToTarget !== null
      ? (lowerIsBetter ? percentToTarget <= 1 : percentToTarget >= 1)
      : null;

  // quick-260625-son Task 2 (option a): the snapshot-date pipeline tiles
  // (MAIN-13..18) only render the trend once enough daily snapshots have
  // accrued. Until then, show the big current value + a muted "trend building"
  // note instead of a sparse, hard-to-read sparkline. Count DISTINCT snap dates
  // so duplicate same-day rows don't prematurely satisfy the threshold.
  const distinctSnapPoints = new Set(snapshots.map((s) => s.snap_date)).size;
  const trendBuildingMin = TREND_BUILDING_MIN[metricId];
  const isTrendBuilding =
    trendBuildingMin != null && distinctSnapPoints < trendBuildingMin;

  // Sparkline: use snap snapshots if provided, otherwise flat empty array.
  // Suppressed entirely while a gated tile is still in the trend-building state.
  const sparklineData = !isTrendBuilding && snapshots.length > 0
    ? snapshots.map((s) => s.value)
    : [];
  const sparklineDates = snapshots.map((s) => s.snap_date);

  // quick-260625-hz9 Task 6: MAIN-13..16 (CQ/NQ pipeline) show month ticks;
  // MAIN-17/18 (FY pipeline) show quarter ticks. All other trend tiles keep
  // the legacy hidden axis.
  const axisName = SPARKLINE_AXIS_NAME[metricId];
  const yAxisName = SPARKLINE_Y_AXIS_NAME[metricId];
  const valueSuffix = SPARKLINE_COUNT_ANNOTATED[metricId];
  // quick-260630-f7f Task 3: per-metric Y-axis legibility flags. Currency ticks
  // for Open Mature Pipeline $ (item 20); a fixed 0–100% scale for the
  // 0–100-style percent tile (item 9). Funnel/response tiles (item 7) just get
  // the visible data-band Y-axis via SPARKLINE_Y_AXIS_NAME above.
  const yAxisCurrency = SPARKLINE_Y_AXIS_CURRENCY.has(metricId);
  const yAxisPercent0100 = SPARKLINE_Y_AXIS_PERCENT_0_100.has(metricId);
  // Per-point sample sizes (n) for count-annotated tiles (SPARKLINE_COUNT_ANNOTATED) — the
  // weekly cohort lead counts ride along on each snapshot's opp_count.
  const sparklineCounts =
    valueSuffix != null ? snapshots.map((s) => s.opp_count) : undefined;
  const sparklineOption = sparklineData.length > 0
    ? buildEchartsOption(
        'sparkline',
        sparklineData,
        data.target_value ?? undefined,
        {
          dates: sparklineDates,
          axisMode: SPARKLINE_AXIS_MODE[metricId] ?? 'hidden',
          ...(axisName != null ? { xAxisName: axisName } : {}),
          ...(yAxisName != null ? { yAxisName } : {}),
          ...(yAxisCurrency ? { yAxisCurrency: true } : {}),
          ...(yAxisPercent0100 ? { yAxisPercent: true, yMin: 0, yMax: 1 } : {}),
          ...(sparklineCounts != null
            ? { counts: sparklineCounts, valueSuffix }
            : {}),
        },
      )
    : null;

  // quick-260626-cot Task 2.1: inline WoW delta chip next to the headline value.
  // Render ONLY when the snapshot series yields a real prior point — never a
  // fabricated 0%/placeholder. Suppressed while a tile is still trend-building
  // (the prior point would be noise from a sub-2-week series). For
  // lower-is-better metrics a numeric increase is BAD (red), so the up/good
  // mapping flips.
  const snapDelta = !isTrendBuilding ? computeSnapDelta(snapshots) : null;
  let deltaChip: { label: string; cls: string; glyph: string } | null = null;
  if (snapDelta) {
    const pct = Math.round(Math.abs(snapDelta.ratio) * 100);
    if (snapDelta.flat) {
      deltaChip = { label: 'flat', cls: 'bg-ink-100 text-ink-500', glyph: '—' };
    } else {
      const isGood = lowerIsBetter ? !snapDelta.up : snapDelta.up;
      deltaChip = {
        label: `${String(pct)}% WoW`,
        cls: isGood ? 'bg-good-bg text-good-text' : 'bg-bad-bg text-bad-text',
        glyph: snapDelta.up ? '▲' : '▼',
      };
    }
  }

  // quick-260626-cot Task 2.1: filler-zone variant selection, in priority order.
  //   (a) area sparkline  — snapshot history present (and not trend-building)
  //   (b) progress bar    — no history but a numeric target exists
  //   (d) trend-building   — gated snap tile still accruing daily points
  // (c) mini bars is folded into (a): the sparkline IS the weekly/count visual.
  // No-target / no-history volume tiles fall through to no filler (per mock).
  // Coverage-style pipeline tiles (MAIN-13/14/15/16/18) match the CRO
  // Pipeline-Coverage look: NEVER a sparkline / "trend building" note — just the
  // big number + WoW pill + (when a target exists) the progress-to-target bar.
  const isCoverageStyle = COVERAGE_STYLE_IDS.has(metricId);
  const fillerVariant: 'sparkline' | 'progress' | 'building' | 'none' =
    isCoverageStyle
      ? hasTarget
        ? 'progress'
        : 'none'
      : isTrendBuilding
        ? 'building'
        : sparklineOption
          ? 'sparkline'
          : hasTarget
            ? 'progress'
            : 'none';

  // Progress-to-target bar geometry (variant b). Clamp the fill to [0,100]% but
  // keep the raw ratio for the label. The target tick sits at 100%.
  const progressPct =
    percentToTarget !== null
      ? Math.max(0, Math.min(100, Math.round(percentToTarget * 100)))
      : 0;
  // For lower-is-better metrics, being OVER target (ratio > 1) is bad → red fill
  // (mock's .fill.over). For normal metrics exceeding the target is good, so the
  // fill stays the brand cyan even past 100%.
  const progressOver =
    lowerIsBetter && percentToTarget !== null && percentToTarget > 1;

  // quick-260630-f7f Task 8 (item 15): SKO baseline comparison SCAFFOLD for
  // MAIN-22/23. `hasSkoBaseline` is true only for tiles in the SKO_BASELINE map;
  // a non-null baseline renders a "vs SKO baseline X%" chip, a null one renders a
  // clearly-marked "SKO baseline: TBD (pending SKO deck)" placeholder. NEVER a
  // fabricated number.
  const hasSkoBaseline = Object.prototype.hasOwnProperty.call(SKO_BASELINE, metricId);
  const skoBaseline = hasSkoBaseline ? SKO_BASELINE[metricId] : undefined;

  return (
    <Card data-testid={`main-tile-${metricId}`}>
      <CardHeader>
        <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
          {title}
        </CardTitle>
        {tooltipFormula && (
          <Tooltip trigger="?" label="Show definition">{tooltipContent}</Tooltip>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        {/* Value row — 38px headline (text-4xl) + inline WoW delta chip.
            The chip renders only when the snapshot series yields a real prior
            point (computeSnapDelta), never a fabricated 0% placeholder. */}
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <p
            className="text-4xl font-bold leading-none tracking-tight tabular-nums text-ink-900"
            data-testid="main-kpi-value"
          >
            {formattedValue}
          </p>
          {deltaChip && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${deltaChip.cls}`}
              data-testid="wow-delta-chip"
            >
              <span aria-hidden>{deltaChip.glyph}</span> {deltaChip.label}
            </span>
          )}
        </div>

        {/* Context line — period label + (when present) the target. */}
        <p className="mt-2 text-xs text-ink-500">
          {data.period_label}
          {hasTarget && data.target_value !== null && (
            <span data-testid="target-label">
              {' · '}vs target {formatMainValue(data.target_value, data.unit)}
              {data.target_text ? ` (${data.target_text})` : ''}
            </span>
          )}
        </p>

        {/* quick-260630-f7f: per-metric clarifier note. MAIN-01 weekly meetings
            goal math (item 2); MAIN-21 point-in-time caveat (item 14). */}
        {TILE_CONTEXT_NOTE[metricId] && (
          <p className="mt-0.5 text-xs text-ink-400" data-testid="tile-context-note">
            {TILE_CONTEXT_NOTE[metricId]}
          </p>
        )}

        {/* quick-260630-f7f Task 8 (item 15): SKO baseline comparison scaffold.
            Real baseline → "vs SKO baseline X%" chip; null → a clearly-marked TBD
            placeholder. No invented numbers. */}
        {hasSkoBaseline && (
          skoBaseline != null ? (
            <span
              className="mt-1.5 inline-flex w-fit items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-brand-700"
              data-testid="sko-baseline-chip"
            >
              vs SKO baseline {(skoBaseline * 100).toFixed(0)}%
            </span>
          ) : (
            <p
              className="mt-1.5 text-xs italic text-ink-400"
              data-testid="sko-baseline-tbd"
            >
              SKO baseline: TBD (pending SKO deck)
            </p>
          )
        )}

        {/* % to target chip + gap. For lower-is-better metrics the color band
            uses the inverted ratio and the arrow reflects on-track direction
            (down = good when under a lower-is-better target). */}
        {hasTarget && chipRatio !== null && (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ${chipBgClass[targetChipVariant(chipRatio)]}`}
              data-testid="percent-chip"
              aria-label={`${formatPercentToTarget(percentToTarget)} of target`}
            >
              {(onTrack ? '▲' : '▼')} {formatPercentToTarget(percentToTarget)} of target
            </span>

            {/* Gap to target — gap = target − value. gap > 0 = UNDER target;
                gap < 0 = OVER. For lower-is-better metrics being OVER target is
                BAD (red). */}
            {data.target_value !== null && (() => {
              const gap = data.target_value - value;
              if (gap === 0) return null;
              const overIsBad = lowerIsBetter;
              return (
                <span className="text-xs text-ink-600" data-testid="gap-to-target">
                  {gap > 0 ? (
                    <>Gap: <span className="font-semibold tabular-nums text-ink-900">{formatMainValue(gap, data.unit)}</span></>
                  ) : (
                    <span className={`font-semibold ${overIsBad ? 'text-bad-text' : 'text-good-text'}`}>
                      {formatMainValue(Math.abs(gap), data.unit)} over target
                    </span>
                  )}
                </span>
              );
            })()}
          </div>
        )}

        {/* ── Filler zone — pinned to the bottom (mt-auto) so equal-height
            cards never leave a lonely number in empty space. One of:
            (a) area sparkline, (b) progress-to-target bar, (d) trend-building
            note, or nothing for no-target/no-history volume tiles. */}
        <div className="mt-auto pt-3.5">
          {fillerVariant === 'building' && (
            <p
              className="text-xs italic text-ink-400"
              data-testid="trend-building-note"
            >
              Trend building — fills in as snapshots accrue ({distinctSnapPoints} of {trendBuildingMin ?? MIN_TREND_POINTS})
            </p>
          )}

          {fillerVariant === 'sparkline' && sparklineOption && (
            // quick-260701-dea FIX D/F: sparkline height 54px → 80px. GLOBAL bump
            // (applies to ALL MainTrendTile sparklines) so the lead-funnel tiles
            // (MAIN-22/23/10/47) and Open-Mature-Pipeline tiles (MAIN-14/16/18)
            // are legible. Safe uniformly because the filler zone is bottom-pinned
            // (mt-auto) and cards stretch to the tallest in the row (items-stretch),
            // so a uniform taller sparkline keeps rows visually even.
            <div className="h-[80px] w-full" data-testid="sparkline-container">
              <ReactECharts
                option={sparklineOption}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'canvas' }}
                notMerge
                lazyUpdate={false}
              />
            </div>
          )}

          {fillerVariant === 'progress' && (
            <div data-testid="progress-to-target">
              <div className="relative mb-2 h-[7px] overflow-visible rounded-full bg-ink-100">
                <div
                  className={`h-[7px] rounded-full ${progressOver ? 'bg-bad-text' : 'bg-adeptia-cyan'}`}
                  style={{ width: `${String(progressPct)}%` }}
                />
                {/* Target tick at 100%. */}
                <div
                  aria-hidden
                  className="absolute top-[-3px] h-[13px] w-0.5 rounded-sm bg-ink-700"
                  style={{ left: '100%' }}
                />
              </div>
              <div className="flex justify-between text-[10.5px] text-ink-400">
                <span>0</span>
                <span>{formatPercentToTarget(percentToTarget ?? 0)} to target</span>
                <span>{data.target_value !== null ? formatMainValue(data.target_value, data.unit) : ''}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
